/**
 * KONSERTENS REGNESTYKKE (SPILLESTED-KONSERT §2 «Budsjett», agent E).
 *
 * Én ren funksjon som API-et og nettleseren regner med, slik at scenariokortene på skjermen og
 * tallet i konsertlista aldri kan bli uenige.
 *
 * ALT ER HELE ØRE, OG HVERT AVLEDET TALL AVRUNDES ÉN GANG. Prosentene kommer inn som
 * basispunkter (6 % = 600, 70 % = 7000), så avgiften er `brutto × bp / 10 000` regnet på heltall
 * og avrundet én gang, halvt bort fra null. Ingen flyttall mellom to avrundinger.
 *
 * LEIE ER ET VALG (skrevet i rapporten): artisten (byrået) leier konserten. Arrangøren får
 * garantien G fast, og artisten får resten — netto − kostnader − G — også når den er negativ.
 */

export const CONCERT_DEAL_TYPES = ['guarantee', 'versus', 'door_split', 'rent'] as const;
export type ConcertDealType = (typeof CONCERT_DEAL_TYPES)[number];

/** Avtalen med hovedartisten, klar til å regnes med. */
export interface DealTerms {
  type: ConcertDealType;
  /** Garantien (eller husleien arrangøren får ved `rent`), i øre. */
  guaranteeMinor: number;
  /** Artistens andel i basispunkter: 70 % = 7000. */
  artistPercentBp: number;
  /** Bare dørsplitt: prosenten regnes av netto − kostnader. */
  afterCosts: boolean;
}

export interface EconomicsInput {
  ticketPriceMinor: number | null;
  /** Billettavgift og gebyr i basispunkter: 6 % = 600. */
  ticketFeeBp: number;
  capacity: number | null;
  expectedAudience: number | null;
  /** Null gir standarden: en tredjedel av kapasiteten. */
  lowAudience: number | null;
  /** Alle kostnader: manuelle linjer, linjene som hentes av seg selv og supportenes garantier. */
  costsMinor: number;
  /** Null: ingen avtale med hovedartisten. Artisten får da ingenting i regnestykket. */
  deal: DealTerms | null;
}

export interface TicketOutcome {
  tickets: number;
  grossMinor: number;
  feesMinor: number;
  netMinor: number;
  costsMinor: number;
  /** Grunnlaget prosenten regnes av (netto, eller netto − kostnader). Null uten prosent. */
  splitBaseMinor: number | null;
  /** Prosenten av grunnlaget, før garantien sammenlignes. Null uten prosent. */
  percentShareMinor: number | null;
  artistShareMinor: number;
  /** Om det er garantien som gjelder (max-en falt på G). */
  guaranteeApplies: boolean;
  resultMinor: number;
}

export interface ConcertEconomics {
  scenarios: {
    low: TicketOutcome | null;
    estimate: TicketOutcome | null;
    soldOut: TicketOutcome | null;
  };
  /** Minste antall billetter der resultatet er ≥ 0. Null når det ikke nås innen kapasiteten. */
  breakEvenTickets: number | null;
  /** Taket break-even ble søkt innenfor: kapasiteten, eller `BREAK_EVEN_CEILING` uten den. */
  breakEvenCeiling: number;
}

/** Uten kapasitet søkes break-even opp til dette. Et hus med flere billetter har en kapasitet. */
export const BREAK_EVEN_CEILING = 1_000_000;

/** Heltallsdivisjon avrundet halvt bort fra null. `d` er alltid positiv her. */
export function roundDiv(n: number, d: number): number {
  if (d <= 0) throw new RangeError('roundDiv: divisor must be positive');
  const sign = n < 0 ? -1 : 1;
  const abs = Math.abs(n);
  const q = Math.floor(abs / d);
  const r = abs - q * d;
  const rounded = r * 2 >= d ? q + 1 : q;
  /* Aldri −0: det skrives «-0 kr» av Intl. */
  return rounded === 0 ? 0 : sign * rounded;
}

/** «70.00», 70 eller null → basispunkter (7000). Prosent med to desimaler, som kolonnen. */
export function percentToBp(value: string | number | null | undefined): number {
  if (value === null || value === undefined || value === '') return 0;
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100);
}

/** Basispunkter → prosent som tall (7000 → 70). */
export function bpToPercent(bp: number): number {
  return bp / 100;
}

/** `amount × bp / 10 000`, avrundet én gang. */
export function percentOf(amountMinor: number, bp: number): number {
  return roundDiv(amountMinor * bp, 10_000);
}

export function computeOutcome(input: EconomicsInput, tickets: number): TicketOutcome {
  const price = input.ticketPriceMinor ?? 0;
  const grossMinor = tickets * price;
  const feesMinor = percentOf(grossMinor, input.ticketFeeBp);
  const netMinor = grossMinor - feesMinor;
  const costsMinor = input.costsMinor;
  const deal = input.deal;

  let splitBaseMinor: number | null = null;
  let percentShareMinor: number | null = null;
  let artistShareMinor = 0;
  let guaranteeApplies = false;

  if (deal !== null) {
    const g = deal.guaranteeMinor;
    switch (deal.type) {
      case 'guarantee':
        artistShareMinor = g;
        guaranteeApplies = true;
        break;
      case 'versus':
      case 'door_split': {
        splitBaseMinor =
          deal.type === 'door_split' && deal.afterCosts ? netMinor - costsMinor : netMinor;
        percentShareMinor = percentOf(splitBaseMinor, deal.artistPercentBp);
        /*
         * VERSUS ER GARANTIEN MOT ANDELEN; DØRSPLITT ER EN DELING AV DØRA (QA runde 1, P3). En
         * dørsplitt uten garanti er en ren andel — aldri «garantien gjelder», og aldri negativ.
         */
        if (deal.type === 'door_split' && g <= 0) {
          guaranteeApplies = false;
          artistShareMinor = Math.max(0, percentShareMinor);
          break;
        }
        guaranteeApplies = g >= percentShareMinor;
        artistShareMinor = guaranteeApplies ? g : percentShareMinor;
        break;
      }
      case 'rent':
        artistShareMinor = netMinor - costsMinor - g;
        break;
    }
  }

  return {
    tickets,
    grossMinor,
    feesMinor,
    netMinor,
    costsMinor,
    splitBaseMinor,
    percentShareMinor,
    artistShareMinor,
    guaranteeApplies,
    resultMinor: netMinor - costsMinor - artistShareMinor,
  };
}

/**
 * Minste antall billetter der arrangøren går i null eller pluss.
 *
 * Resultatet stiger aldri ned når én billett til selges (artistens andel av en ekstra krone er
 * høyst hele kronen), så et binærsøk er riktig. Null når taket er nådd uten å komme i null.
 */
export function breakEvenTickets(input: EconomicsInput, ceiling: number): number | null {
  if (computeOutcome(input, 0).resultMinor >= 0) return 0;
  if (computeOutcome(input, ceiling).resultMinor < 0) return null;
  let lo = 0; // resultatet < 0
  let hi = ceiling; // resultatet ≥ 0
  while (hi - lo > 1) {
    const mid = Math.floor((lo + hi) / 2);
    if (computeOutcome(input, mid).resultMinor >= 0) hi = mid;
    else lo = mid;
  }
  return hi;
}

/** «Hvis 400 kommer»: det lave scenariet, med standarden en tredjedel av kapasiteten. */
export function lowScenarioTickets(
  input: Pick<EconomicsInput, 'lowAudience' | 'capacity'>,
): number | null {
  if (input.lowAudience !== null) return input.lowAudience;
  if (input.capacity !== null) return Math.floor(input.capacity / 3);
  return null;
}

export function computeConcertEconomics(input: EconomicsInput): ConcertEconomics {
  const low = lowScenarioTickets(input);
  const ceiling = input.capacity ?? BREAK_EVEN_CEILING;
  return {
    scenarios: {
      low: low === null ? null : computeOutcome(input, low),
      estimate:
        input.expectedAudience === null ? null : computeOutcome(input, input.expectedAudience),
      soldOut: input.capacity === null ? null : computeOutcome(input, input.capacity),
    },
    breakEvenTickets: breakEvenTickets(input, ceiling),
    breakEvenCeiling: ceiling,
  };
}

/**
 * «70/30» for en prosentavtale, ellers null. Språknøytralt: typen oversettes der den vises
 * (`concertBudget.dealType.*`), og API-et returnerer aldri en oversatt setning.
 */
export function dealSplitLabel(type: ConcertDealType, artistPercentBp: number): string | null {
  if (type !== 'versus' && type !== 'door_split') return null;
  const artist = bpToPercent(artistPercentBp);
  const promoter = bpToPercent(10_000 - artistPercentBp);
  return `${formatPercentNumber(artist)}/${formatPercentNumber(promoter)}`;
}

function formatPercentNumber(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
}
